import React, { useEffect, useState } from 'react';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

function buildUrl(path) {
  return `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
}

async function apiFetch(path, options = {}) {
  const token = localStorage.getItem('accessToken');
  const response = await fetch(buildUrl(path), {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const fieldMessage = data && typeof data === 'object'
      ? Object.values(data).flat().find((value) => typeof value === 'string')
      : null;
    throw new Error(data?.detail || data?.email?.[0] || fieldMessage || `Request failed (${response.status})`);
  }
  return data;
}

const EMPTY_EMPLOYEE = {
  id: null,
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  is_active: true,
  receive_daily_email: true,
};

export default function TodoTeamSettings() {
  const [employees, setEmployees] = useState([]);
  const [rules, setRules] = useState([]);
  const [templateDefinitions, setTemplateDefinitions] = useState([]);
  const [employeeForm, setEmployeeForm] = useState(EMPTY_EMPLOYEE);
  const [templateEmployeeId, setTemplateEmployeeId] = useState(null);
  const [templateDraft, setTemplateDraft] = useState({});
  const [templateOfficeAddress, setTemplateOfficeAddress] = useState('');
  const [templateOfficeDirections, setTemplateOfficeDirections] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingEmployee, setSavingEmployee] = useState(false);
  const [savingRules, setSavingRules] = useState(false);
  const [savingTemplates, setSavingTemplates] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiFetch('/firm/todo-settings/');
      setEmployees(Array.isArray(data?.employees) ? data.employees : []);
      setRules(Array.isArray(data?.rules) ? data.rules : []);
      setTemplateDefinitions(Array.isArray(data?.text_template_definitions) ? data.text_template_definitions : []);
    } catch (err) {
      setError(err.message || 'Could not load employee settings.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const flash = (text) => {
    setMessage(text);
    window.setTimeout(() => setMessage(''), 2500);
  };

  const editEmployee = (employee) => {
    setEmployeeForm({
      id: employee.id,
      first_name: employee.first_name || '',
      last_name: employee.last_name || '',
      email: employee.email || '',
      phone: employee.phone || '',
      is_active: employee.is_active !== false,
      receive_daily_email: employee.receive_daily_email !== false,
    });
  };

  const saveEmployee = async (event) => {
    event.preventDefault();
    setSavingEmployee(true);
    setError('');
    const payload = {
      first_name: employeeForm.first_name.trim(),
      last_name: employeeForm.last_name.trim(),
      email: employeeForm.email.trim(),
      phone: employeeForm.phone.trim(),
      is_active: employeeForm.is_active,
      receive_daily_email: employeeForm.receive_daily_email,
    };
    try {
      if (employeeForm.id) {
        await apiFetch(`/firm/employees/${employeeForm.id}/`, {
          method: 'PATCH',
          body: JSON.stringify(payload),
        });
        flash('Employee updated.');
      } else {
        await apiFetch('/firm/employees/', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        flash('Employee added to the round-robin pools.');
      }
      setEmployeeForm(EMPTY_EMPLOYEE);
      await load();
    } catch (err) {
      setError(err.message || 'Could not save employee.');
    } finally {
      setSavingEmployee(false);
    }
  };

  const deactivateEmployee = async (employee) => {
    if (!window.confirm(`Deactivate ${employee.full_name}? Their completed-task history will be preserved.`)) return;
    setError('');
    try {
      await apiFetch(`/firm/employees/${employee.id}/`, { method: 'DELETE' });
      flash('Employee deactivated. Open work will be reassigned.');
      await load();
    } catch (err) {
      setError(err.message || 'Could not deactivate employee.');
    }
  };

  const reactivateEmployee = async (employee) => {
    setError('');
    try {
      await apiFetch(`/firm/employees/${employee.id}/`, {
        method: 'PATCH',
        body: JSON.stringify({ is_active: true }),
      });
      flash('Employee reactivated.');
      await load();
    } catch (err) {
      setError(err.message || 'Could not reactivate employee.');
    }
  };

  const updateRule = (taskType, changes) => {
    setRules((current) => current.map((rule) => (
      rule.task_type === taskType ? { ...rule, ...changes } : rule
    )));
  };

  const toggleRuleEmployee = (taskType, employeeId) => {
    const rule = rules.find((item) => item.task_type === taskType);
    if (!rule) return;
    const selected = rule.employee_ids || [];
    const next = selected.includes(employeeId)
      ? selected.filter((id) => id !== employeeId)
      : [...selected, employeeId];
    updateRule(taskType, { employee_ids: next });
  };

  const saveRules = async () => {
    setSavingRules(true);
    setError('');
    try {
      const data = await apiFetch('/firm/todo-settings/', {
        method: 'PATCH',
        body: JSON.stringify({ rules }),
      });
      setEmployees(Array.isArray(data?.employees) ? data.employees : employees);
      setRules(Array.isArray(data?.rules) ? data.rules : rules);
      flash('Todo limits and assignments saved.');
    } catch (err) {
      setError(err.message || 'Could not save assignment settings.');
    } finally {
      setSavingRules(false);
    }
  };

  const openTextTemplates = (employee) => {
    const savedTemplates = employee.todo_text_templates || {};
    setTemplateDraft(Object.fromEntries(
      templateDefinitions.map((definition) => [
        definition.key,
        savedTemplates[definition.key] || '',
      ]),
    ));
    setTemplateOfficeAddress(employee.office_address || '');
    setTemplateOfficeDirections(employee.office_directions || '');
    setTemplateEmployeeId(employee.id);
    setError('');
  };

  const updateTemplate = (key, value) => {
    setTemplateDraft((current) => ({ ...current, [key]: value }));
  };

  const saveTextTemplates = async () => {
    if (!templateEmployeeId) return;
    setSavingTemplates(true);
    setError('');
    try {
      const updated = await apiFetch(`/firm/employees/${templateEmployeeId}/`, {
        method: 'PATCH',
        body: JSON.stringify({
          todo_text_templates: templateDraft,
          office_address: templateOfficeAddress.trim(),
          office_directions: templateOfficeDirections.trim(),
        }),
      });
      setEmployees((current) => current.map((employee) => (
        employee.id === updated.id ? updated : employee
      )));
      setTemplateEmployeeId(null);
      setTemplateDraft({});
      setTemplateOfficeAddress('');
      setTemplateOfficeDirections('');
      flash('Employee reminder texts saved.');
    } catch (err) {
      setError(err.message || 'Could not save employee reminder texts.');
    } finally {
      setSavingTemplates(false);
    }
  };

  if (loading) {
    return <section className="rounded-lg bg-white p-6 shadow">Loading employee todo settings…</section>;
  }

  const activeEmployees = employees.filter((employee) => employee.is_active);
  const templateEmployee = employees.find((employee) => employee.id === templateEmployeeId) || null;
  const templatePlaceholders = templateDefinitions[0]?.placeholders || [];

  return (
    <section className="space-y-6 rounded-lg bg-white p-6 shadow">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Employees & Today&apos;s ToDos</h2>
        <p className="mt-1 text-sm text-gray-600">
          Add assistants, choose their task pools, and control how much work appears each day.
          Employees are assigned individual tasks round-robin.
        </p>
      </div>

      {message && <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">{message}</div>}
      {error && <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>}

      <form onSubmit={saveEmployee} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">{employeeForm.id ? 'Edit employee' : 'Add employee'}</h3>
          {employeeForm.id && (
            <button type="button" onClick={() => setEmployeeForm(EMPTY_EMPLOYEE)} className="text-sm text-gray-600 hover:text-gray-900">
              Cancel editing
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <input
            required
            value={employeeForm.first_name}
            onChange={(event) => setEmployeeForm((current) => ({ ...current, first_name: event.target.value }))}
            placeholder="First name"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
          <input
            required
            value={employeeForm.last_name}
            onChange={(event) => setEmployeeForm((current) => ({ ...current, last_name: event.target.value }))}
            placeholder="Last name"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
          <input
            required
            type="email"
            value={employeeForm.email}
            onChange={(event) => setEmployeeForm((current) => ({ ...current, email: event.target.value }))}
            placeholder="Email"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
          <input
            value={employeeForm.phone}
            onChange={(event) => setEmployeeForm((current) => ({ ...current, phone: event.target.value }))}
            placeholder="Phone (optional)"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-5">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={employeeForm.receive_daily_email}
              onChange={(event) => setEmployeeForm((current) => ({ ...current, receive_daily_email: event.target.checked }))}
            />
            Receive morning todo email
          </label>
          {employeeForm.id && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={employeeForm.is_active}
                onChange={(event) => setEmployeeForm((current) => ({ ...current, is_active: event.target.checked }))}
              />
              Active employee
            </label>
          )}
          <button
            type="submit"
            disabled={savingEmployee}
            className="ml-auto rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
          >
            {savingEmployee ? 'Saving…' : employeeForm.id ? 'Save employee' : 'Add employee'}
          </button>
        </div>
      </form>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-700">
            <tr>
              <th className="px-4 py-3">Employee</th>
              <th className="px-4 py-3">Contact</th>
              <th className="px-4 py-3">Morning email</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {employees.map((employee) => (
              <tr key={employee.id} className="border-t border-gray-200">
                <td className="px-4 py-3 font-medium text-gray-900">{employee.full_name}</td>
                <td className="px-4 py-3 text-gray-600">{employee.email}<br />{employee.phone || '—'}</td>
                <td className="px-4 py-3 text-gray-600">{employee.receive_daily_email ? 'Yes' : 'No'}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2 py-1 text-xs font-semibold ${employee.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                    {employee.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => openTextTemplates(employee)} className="mr-3 text-indigo-700 hover:underline">Reminder texts</button>
                  <button onClick={() => editEmployee(employee)} className="mr-3 text-indigo-700 hover:underline">Edit</button>
                  {employee.is_active ? (
                    <button onClick={() => deactivateEmployee(employee)} className="text-red-700 hover:underline">Deactivate</button>
                  ) : (
                    <button onClick={() => reactivateEmployee(employee)} className="text-green-700 hover:underline">Reactivate</button>
                  )}
                </td>
              </tr>
            ))}
            {employees.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-500">No employees added yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div>
        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">Daily limits & round-robin pools</h3>
            <p className="text-sm text-gray-600">A limit of 0 means include every eligible item. Select more than one employee to distribute that category round-robin.</p>
          </div>
          <button
            type="button"
            onClick={saveRules}
            disabled={savingRules}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
          >
            {savingRules ? 'Saving…' : 'Save todo settings'}
          </button>
        </div>

        <div className="space-y-3">
          {rules.map((rule) => (
            <div key={rule.task_type} className="grid grid-cols-1 gap-3 rounded-lg border border-gray-200 p-4 md:grid-cols-[minmax(240px,1fr)_130px_2fr]">
              <div className="font-medium text-gray-900">{rule.label}</div>
              <label className="text-sm text-gray-700">
                Daily limit
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={rule.daily_limit}
                  onChange={(event) => updateRule(rule.task_type, { daily_limit: Number(event.target.value) })}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
                />
              </label>
              <div>
                <div className="mb-1 text-sm text-gray-700">Assigned employees</div>
                <div className="flex flex-wrap gap-3">
                  {activeEmployees.map((employee) => (
                    <label key={employee.id} className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={(rule.employee_ids || []).includes(employee.id)}
                        onChange={() => toggleRuleEmployee(rule.task_type, employee.id)}
                      />
                      {employee.full_name}
                    </label>
                  ))}
                  {activeEmployees.length === 0 && <span className="text-sm text-amber-700">Add an active employee first.</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {templateEmployee && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  Reminder texts for {templateEmployee.full_name}
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  These versions are used whenever this employee is assigned the todo. Leave a text blank to keep using the automatic default.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setTemplateEmployeeId(null)}
                disabled={savingTemplates}
                className="text-2xl leading-none text-gray-500 hover:text-gray-900 disabled:opacity-50"
                aria-label="Close reminder text editor"
              >
                ×
              </button>
            </div>

            <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
              <h4 className="text-sm font-semibold text-blue-900">Information filled in automatically</h4>
              <p className="mt-1 text-xs text-blue-800">
                Put these placeholders anywhere in a message. They will be replaced with the assigned employee, client, appointment, office, or 341 information.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {templatePlaceholders.map((placeholder) => (
                  <code key={placeholder} className="rounded bg-white px-2 py-1 text-xs text-blue-900 ring-1 ring-blue-200">
                    {`{${placeholder}}`}
                  </code>
                ))}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4 md:grid-cols-2">
              <label className="text-sm font-semibold text-gray-800">
                This employee&apos;s office address
                <input
                  value={templateOfficeAddress}
                  onChange={(event) => setTemplateOfficeAddress(event.target.value)}
                  placeholder="Leave blank to use the firm address"
                  className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-normal"
                />
                <span className="mt-1 block text-xs font-normal text-gray-500">Used for the {'{office_address}'} placeholder.</span>
              </label>
              <label className="text-sm font-semibold text-gray-800">
                This employee&apos;s office directions
                <textarea
                  rows={3}
                  value={templateOfficeDirections}
                  onChange={(event) => setTemplateOfficeDirections(event.target.value)}
                  placeholder="Leave blank to use the current default directions"
                  className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-normal"
                />
                <span className="mt-1 block text-xs font-normal text-gray-500">Used for the {'{office_directions}'} placeholder.</span>
              </label>
            </div>

            <div className="mt-5 space-y-3">
              {templateDefinitions.map((definition) => {
                const customized = Boolean((templateDraft[definition.key] || '').trim());
                return (
                  <details key={definition.key} className="rounded-lg border border-gray-200 bg-gray-50">
                    <summary className="flex cursor-pointer items-center justify-between gap-3 px-4 py-3">
                      <span>
                        <span className="font-semibold text-gray-900">{definition.label}</span>
                        <span className="mt-0.5 block text-xs text-gray-600">{definition.description}</span>
                      </span>
                      <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${customized ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-200 text-gray-700'}`}>
                        {customized ? 'Customized' : 'Using default'}
                      </span>
                    </summary>
                    <div className="border-t border-gray-200 p-4">
                      <textarea
                        rows={7}
                        value={templateDraft[definition.key] || ''}
                        onChange={(event) => updateTemplate(definition.key, event.target.value)}
                        placeholder={definition.default_text}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm leading-6 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                      />
                      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                        <p className="text-xs text-gray-500">Blank means the displayed default will be used automatically.</p>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => updateTemplate(definition.key, definition.default_text)}
                            className="rounded border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100"
                          >
                            Load default to edit
                          </button>
                          <button
                            type="button"
                            onClick={() => updateTemplate(definition.key, '')}
                            className="rounded border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100"
                          >
                            Use automatic default
                          </button>
                        </div>
                      </div>
                    </div>
                  </details>
                );
              })}
            </div>

            <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-4">
              <button
                type="button"
                onClick={() => setTemplateEmployeeId(null)}
                disabled={savingTemplates}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveTextTemplates}
                disabled={savingTemplates}
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
              >
                {savingTemplates ? 'Saving…' : 'Save reminder texts'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
